import Util from '@/util'

const HEX_SHORT = /^#([a-f\d])([a-f\d])([a-f\d])$/i
const HEX_LONG = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i
const RGB_RE = /^rgba?\(\s*([\d.]+)[\s,/]+([\d.]+)[\s,/]+([\d.]+)/i
const HSL_RE = /^hsla?\(\s*([\d.]+)(?:deg)?[\s,/]+([\d.]+)%[\s,/]+([\d.]+)%/i

function parseHex(input) {
  const short = HEX_SHORT.exec(input)
  if (short) {
    return {
      r: parseInt(short[1] + short[1], 16),
      g: parseInt(short[2] + short[2], 16),
      b: parseInt(short[3] + short[3], 16),
    }
  }
  const long = HEX_LONG.exec(input)
  if (long) {
    return {
      r: parseInt(long[1], 16),
      g: parseInt(long[2], 16),
      b: parseInt(long[3], 16),
    }
  }
  return null
}

function hslToRgb(h, s, l) {
  const hue = (((h % 360) + 360) % 360) / 360
  const sat = s / 100
  const light = l / 100
  const chroma = sat * Math.min(light, 1 - light)
  const channel = (n) => {
    const k = (n + hue * 12) % 12
    return light - chroma * Math.max(-1, Math.min(k - 3, 9 - k, 1))
  }
  return {
    r: Math.round(channel(0) * 255),
    g: Math.round(channel(8) * 255),
    b: Math.round(channel(4) * 255),
  }
}

// Parse a CSS colour string (hex, rgb/rgba, hsl/hsla) into an {r, g, b} triple.
// Returns null for named colours or anything we can't statically resolve —
// callers must supply their own fallback.
//
// For hex we first delegate to Util.hexToRgb because the pre-existing
// isColorLight/isContrast specs mock it and assert it was called; only when
// that returns null do we fall back to our own regex (which is what unblocks
// the HSL/rgb/rgba/short-hex paths the mock never covered).
export function parseColor(input) {
  if (typeof input !== 'string') return null
  const s = input.trim()
  // The global __mocks__/util stub doesn't expose hexToRgb, so probe for
  // the function before calling. When present (real code path, or a spec
  // that mocks it via jest.fn()) we let it take the hex path.
  if (typeof Util.hexToRgb === 'function') {
    const utilHex = Util.hexToRgb(s)
    if (utilHex) return utilHex
  }
  const hex = parseHex(s)
  if (hex) return hex
  const rgb = RGB_RE.exec(s)
  if (rgb) return { r: Math.round(+rgb[1]), g: Math.round(+rgb[2]), b: Math.round(+rgb[3]) }
  const hsl = HSL_RE.exec(s)
  if (hsl) return hslToRgb(+hsl[1], +hsl[2], +hsl[3])
  return null
}

function toRgbString(r, g, b) {
  return `rgb(${r}, ${g}, ${b})`
}

// Blend `color` with `target` in sRGB — `amount` is how much of `target`
// (0 = pure color, 1 = pure target). Returns null when `color` can't be parsed.
function blend(color, target, amount) {
  const c = parseColor(color)
  if (!c) return null
  const mix = (a, b) => Math.round(a * (1 - amount) + b * amount)
  return toRgbString(mix(c.r, target.r), mix(c.g, target.g), mix(c.b, target.b))
}

const WHITE = { r: 255, g: 255, b: 255 }
const BLACK = { r: 0, g: 0, b: 0 }

// Opaque light tint — `colorRatio` of the input mixed into white. Used for the
// category card background so it stays legible on both light and dark themes.
export function tintOnWhite(color, colorRatio = 0.08) {
  return blend(color, WHITE, 1 - colorRatio) ?? '#FFFFFF'
}

// Darken by mixing in `blackAmount` of black — used for hover/active shades.
export function shadeWithBlack(color, blackAmount = 0.12) {
  return blend(color, BLACK, blackAmount) ?? color
}

export function isColorLight(hex = '#313131') {
  if (hex === '') {
    hex = '#313131'
  }
  const rgb = parseColor(hex)
  if (!rgb) return false

  // Calculate relative luminance (per WCAG standards)
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
  return luminance > 0.5
}

// Every CSS var a Categorise card needs, precomputed so we don't rely on
// color-mix() at render time (it's not in the current browserslist floor).
export function categoryColorVars(color) {
  return {
    '--category-color': color,
    '--category-text-color': isColorLight(color) ? '#282E3E' : '#FFFFFF',
    '--category-light-bg': tintOnWhite(color, 0.08),
    '--category-hover-color': shadeWithBlack(color, 0.12),
    '--category-active-color': shadeWithBlack(color, 0.22),
    // Dark shade of the palette colour; used as text on a white annotation
    // chip so the letter stays legible even for pale palettes.
    '--category-annotation-color': shadeWithBlack(color, 0.45),
  }
}

export function isContrast(color1 = '#ffffff', color2 = '#000000') {
  // For more info: https://dev.to/alvaromontoro/building-your-own-color-contrast-checker-4j7o
  const hex1 = Util.hexToRgb(color1)
  const hex2 = Util.hexToRgb(color2)

  if (!hex1 || !hex2) return false
  const hexLuminance = Util.getColourLuminance(
    hex1.r,
    hex1.g,
    hex1.b,
  )
  const hex2Luminance = Util.getColourLuminance(
    hex2.r,
    hex2.g,
    hex2.b,
  )
  const contrastRatio = Util.getContrastRatio(
    hexLuminance,
    hex2Luminance,
  )

  // Question title font size's > 18px ( > 18pt) => AA-level passed when contrast < 1/3
  const AALevelPassedForLargeText = 1 / 3
  return contrastRatio < AALevelPassedForLargeText
}
